import React, { useCallback, useMemo, useState } from 'react';
import { buildProfileProxyImageUrl, joinApiAssetPath } from './memberListUtils';
import {
  adminInitialFromName,
  avatarInitialFromName,
  buildAdminAvatarStyle,
  buildArtistAvatarStyle,
  buildMemberModalProfileState,
} from './memberModalProfileUtils';
import MemberListProfileLayer from './MemberListProfileLayer';

export default function useMemberListModalPresentation({
  apiUrl,
  artistProfilePath,
  adminProfilePath,
  isConciergeEvent = false,
  conciergePlanner = null,
  resolveArtistMembershipLabel,
  resolveAdminMembershipLabel,
  resolveConciergeMembershipLabel,
  variant = 'admin',
  allowArtistLink = true,
}) {
  const [profileTarget, setProfileTarget] = useState(null);

  const buildProfileProxyUrl = useCallback(
    (base, file) => buildProfileProxyImageUrl(apiUrl, base, file),
    [apiUrl]
  );
  const joinPath = useCallback(
    (base, file) => joinApiAssetPath(apiUrl, base, file),
    [apiUrl]
  );

  const {
    buildArtistProfile,
    buildAdminProfile,
    conciergeMembers,
  } = useMemo(
    () => buildMemberModalProfileState({
      isConciergeEvent,
      conciergePlanner,
      resolveArtistMembershipLabel,
      resolveAdminMembershipLabel,
      resolveConciergeMembershipLabel,
    }),
    [
      conciergePlanner,
      isConciergeEvent,
      resolveAdminMembershipLabel,
      resolveArtistMembershipLabel,
      resolveConciergeMembershipLabel,
    ]
  );

  const avatarStyle = useCallback((pic, gender) => buildArtistAvatarStyle({
    pic,
    gender,
    buildProfileProxyUrl,
    joinPath,
    artistImageBasePath: artistProfilePath,
  }), [artistProfilePath, buildProfileProxyUrl, joinPath]);

  const adminAvatarStyle = useCallback((pic) => buildAdminAvatarStyle({
    pic,
    buildProfileProxyUrl,
    joinPath,
    adminImageBasePath: adminProfilePath,
  }), [adminProfilePath, buildProfileProxyUrl, joinPath]);

  const closeProfile = useCallback(() => setProfileTarget(null), []);
  const openAdminProfile = useCallback(
    (payload) => setProfileTarget({ type: 'admin', payload }),
    []
  );
  const openArtistProfile = useCallback(
    (payload) => setProfileTarget({ type: 'artist', payload }),
    []
  );

  const profileNode = (
    <MemberListProfileLayer
      profileTarget={profileTarget}
      buildArtistProfile={buildArtistProfile}
      buildAdminProfile={buildAdminProfile}
      onClose={closeProfile}
      variant={variant}
      allowArtistLink={allowArtistLink}
    />
  );

  return {
    profileNode,
    conciergeMembers,
    avatarStyle,
    avatarInitial: avatarInitialFromName,
    adminAvatarStyle,
    adminInitial: adminInitialFromName,
    openAdminProfile,
    openArtistProfile,
  };
}
